import React, { useEffect, useRef, useState } from "react";
import { Spinner, NavLink, Row, Col } from "reactstrap";
import { Helmet } from "react-helmet";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "../../API/axios";
import useNewsFeedStore from "../NewsFeed/useNewsFeedStore";
import { ListRowSkeleton } from "@skeleton";
import { post } from "../../config/imagesLink";

const getTabFromRoute = (routeTab) => {
  const tab = String(routeTab || "").toLowerCase();

  switch (tab) {
    case "people":
      return 1;
    case "companies":
      return 2;
    case "jobs":
      return 3;
    case "posts":
      return 4;
    case "hashtags":
      return 5;
    case "articles":
      return 6;
    case "blogs":
      return 7;
    default:
      return 1;
  }
};

const SEARCH_TAB_ROUTES = {
  1: "People",
  2: "Companies",
  3: "Jobs",
  4: "Posts",
  5: "Hashtags",
  6: "Articles",
  7: "Blogs",
};

const SearchResult = ({ searchQuery = "" }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { tab: routeTab } = useParams();
  const { setIsNewsPageLoaded } = useNewsFeedStore();
  const searchQueryFromUrl = new URLSearchParams(location.search).get("q") || "";
  const searchQueryFromState = location.state?.searchQuery || "";
  const resolvedSearchQuery = searchQuery || searchQueryFromUrl || searchQueryFromState;

  const [activeTab, setActiveTab] = useState(() => getTabFromRoute(routeTab));
  const [isLoading, setIsLoading] = useState(true);
  const [nextBatchLoading, setNextBatchLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [inputValue, setInputValue] = useState(resolvedSearchQuery);
  const [debouncedInputValue, setDebouncedInputValue] = useState(
    resolvedSearchQuery.trim()
  );
  const [hasTabChanged, setHasTabChanged] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [options, setOptions] = useState([]);
  const [searchUser, setSearchUser] = useState([]);
  const [searchHashtag, setSearchHashtag] = useState([]);
  const [searchComp, setSearchComp] = useState([]);
  const [searchJob, setSearchJob] = useState([]);
  const [searchPost, setSearchPost] = useState([]);
  const [searchBlog, setSearchBlog] = useState([]);
  const [searchArticle, setSearchArticle] = useState([]);
  const isFetching = useRef(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    setIsNewsPageLoaded(true);
    return () => setIsNewsPageLoaded(false);
  }, [setIsNewsPageLoaded]);

  useEffect(() => {
    const nextSearchValue = resolvedSearchQuery.trim();

    if (nextSearchValue !== inputValue) {
      setInputValue(nextSearchValue);
    }
  }, [resolvedSearchQuery]);

  useEffect(() => {
    const trimmedValue = inputValue.trim();
    const params = new URLSearchParams(location.search);

    if (trimmedValue) {
      params.set("q", trimmedValue);
    } else {
      params.delete("q");
    }

    const nextSearch = params.toString();
    const normalizedNextSearch = nextSearch ? `?${nextSearch}` : "";

    if (normalizedNextSearch === location.search) {
      return;
    }

    navigate(
      {
        pathname: location.pathname,
        search: normalizedNextSearch,
      },
      {
        replace: true,
        state: {
          ...(location.state || {}),
          searchQuery: trimmedValue,
        },
      }
    );
  }, [inputValue, location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setDebouncedInputValue(inputValue.trim());
    }, 1000);

    return () => clearTimeout(delayDebounce);
  }, [inputValue]);

  const clearResults = () => {
    setSearchUser([]);
    setSearchHashtag([]);
    setSearchComp([]);
    setSearchJob([]);
    setSearchPost([]);
    setSearchBlog([]);
    setSearchArticle([]);
    setOptions([]);
  };

  const addOptions = () => {
    if (isFetching.current) return;

    isFetching.current = true;
    setIsLoading(true);
    const searchValue = debouncedInputValue || "All";
    const data = {
      deviceType: "Web",
      pageNumber: page,
      pageSize: 10,
      langKey: "",
      search:  searchValue,
      // search: hasTabChanged ? "All" : searchValue,
      type:
        activeTab === 1 ? "User" :
        activeTab === 2 ? "Company" :
        activeTab === 3 ? "Job" :
        activeTab === 4 ? "Post" :
        activeTab === 5 ? "Hashtag" :
        activeTab === 6 ? "Article" :
        activeTab === 7 ? "Blog" : "",
    };

    axios
      .post("SMFeatures/GlobalSearchWithFilter", data)
      .then((response) => {
        if (
          !response.data ||
          !response.data.responseData ||
          !response.data.responseData.searchdata
        ) {
          setIsError(true);
          setIsLoading(false);
          setNextBatchLoading(false);
          setHasMoreData(false);
          clearResults();
          return;
        }

        let data = response.data.responseData.searchdata;

        if (Array.isArray(data) && data.length === 0) {
          setIsError(true);
          setIsLoading(false);
          setNextBatchLoading(false);
          setHasMoreData(false);
          clearResults();
          return;
        }

        setIsError(false);
        data = data.map((item) => {
          const [type, followers] = String(item?.type || "").split(",");
          return {
            ...item,
            type: type?.trim(),
            followers: parseInt(followers, 10),
          };
        });

        const addUniqueItems = (prevList, nextList) => {
          if (page === 1) {
            return nextList;
          }

          const itemSet = new Set(prevList.map((item) => item.id));
          const uniqueNewItems = nextList.filter((item) => !itemSet.has(item.id));
          return [...prevList, ...uniqueNewItems];
        };

        const users = data.filter((item) => item.type === "User");
        const hashtags = data.filter((item) => item.type === "Hashtag");
        const companies = data.filter((item) => item.type === "Company");
        const jobs = data.filter((item) => item.type === "Job");
        const posts = data.filter((item) => item.type === "Post");
        const blogs = data.filter((item) => item.type === "Blog");
        const articles = data.filter((item) => item.type === "Article");
        setSearchUser((prev) => addUniqueItems(prev, users));
        setSearchHashtag((prev) => addUniqueItems(prev, hashtags));
        setSearchComp((prev) => addUniqueItems(prev, companies));
        setSearchJob((prev) => addUniqueItems(prev, jobs));
        setSearchPost((prev) => addUniqueItems(prev, posts));
        setSearchBlog((prev) => addUniqueItems(prev, blogs));
        setSearchArticle((prev) => addUniqueItems(prev, articles));

        setIsLoading(false);
        setNextBatchLoading(false);
        const nextTotalPages = response.data.responseData.paging?.totalPages || 1;
        setTotalPages(nextTotalPages);
        setHasMoreData(page < nextTotalPages);

        setOptions((prev) => {
          const postSet = new Set(prev.map((post) => post.id));
          const uniqueNewPosts = data.filter((post) => !postSet.has(post.id));
          return page === 1 ? uniqueNewPosts : [...prev, ...uniqueNewPosts];
        });
      })
      .catch(() => {
        setIsError(true);
        setIsLoading(false);
        setNextBatchLoading(false);
        setHasMoreData(false);
        clearResults();
      })
      .finally(() => {
        isFetching.current = false;
      });
  };

  useEffect(() => {
    clearResults();
    setIsError(false);
    setTotalPages(1);
    setHasMoreData(true);

    if (debouncedInputValue && debouncedInputValue.length < 3) {
      setIsLoading(false);
      setNextBatchLoading(false);
      setHasMoreData(false);
      return;
    }

    if (page !== 1) {
      setPage(1);
      return;
    }

    addOptions();
  }, [debouncedInputValue, activeTab]);

  useEffect(() => {
    if (page > 1) {
      setNextBatchLoading(true);
      addOptions();
    }
  }, [page]);

  useEffect(() => {
    const nextTab = getTabFromRoute(routeTab);

    if (nextTab !== activeTab) {
      setActiveTab(nextTab);
    }
  }, [activeTab, routeTab]);

  const goToSearchTab = (tabIndex) => {
    setHasTabChanged(true);
    setPage(1);
    setActiveTab(tabIndex);
    const tabPath = SEARCH_TAB_ROUTES[tabIndex];
    if (tabPath) {
      navigate({
        pathname: `/SearchResult/${tabPath}`,
        search: location.search,
      });
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  useEffect(() => {
    const handleInfiniteScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop + 10 >=
          document.documentElement.scrollHeight &&
        !isLoading &&
        hasMoreData &&
        !isFetching.current &&
        page < totalPages
      ) {
        if (debounceRef.current) return;

        debounceRef.current = setTimeout(() => {
          setPage((prev) => prev + 1);
          debounceRef.current = null;
        }, 300);
      }
    };

    window.addEventListener("scroll", handleInfiniteScroll);

    return () => {
      window.removeEventListener("scroll", handleInfiniteScroll);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [hasMoreData, isLoading, page, totalPages]);

  const renderEmptyState = () => {
    if (isLoading && page === 1) {
      return (
         <ListRowSkeleton className="mt-3" count={5} rowHeight={80} showAvatar />
      );
    }

    if (isError) {
      return (
         <div className="mt-2 error-image-cont d-flex align-items-center justify-content-center">
              <img src={post.errorSmall} alt="404 Not Found" style={{width: "250px"}}/>
         </div>
      );
    }

    return null;
  };

  const normalizeSearchTitle = (value) => {
    if (value === null || value === undefined) return "";

    const rawValue = String(value);
    let parsedValue = null;

    try {
      parsedValue = JSON.parse(rawValue);
    } catch (error) {
      parsedValue = null;
    }

    if (Array.isArray(parsedValue)) {
      const tags = parsedValue
        .map((item) => item?.displayName)
        .filter(Boolean)
        .map((name) => `#${name}`);

      if (tags.length > 0) {
        return [...new Set(tags)].join(" ");
      }
    }

    let cleanedText = rawValue.replace(/\{\d*\}/g, " ");

    if (typeof DOMParser !== "undefined") {
      const parser = new DOMParser();
      cleanedText = parser.parseFromString(cleanedText, "text/html").body.textContent || cleanedText;
    } else {
      cleanedText = cleanedText.replace(/<[^>]*>/g, " ");
    }

    cleanedText = cleanedText.replace(/\s+/g, " ").trim();

    if (!cleanedText) {
      const hashtags = [...rawValue.matchAll(/\/hashtag\/([^"'/?#\s>]+)/gi)].map((match) => {
        try {
          return `#${decodeURIComponent(match[1])}`;
        } catch (error) {
          return `#${match[1]}`;
        }
      });

      if (hashtags.length > 0) {
        return [...new Set(hashtags)].join(" ");
      }
    }

    return cleanedText;
  };

  const normalizeSearchMeta = (value) => {
    if (value === null || value === undefined) return "";

    const rawValue = String(value).trim();
    if (!rawValue) return "";

    let parsedValue = null;
    try {
      parsedValue = JSON.parse(rawValue);
    } catch (error) {
      parsedValue = null;
    }

    if (Array.isArray(parsedValue)) {
      const tags = parsedValue
        .map((item) => item?.displayName)
        .filter(Boolean)
        .map((name) => `#${name}`);

      return [...new Set(tags)].join(" ");
    }

    if (parsedValue && typeof parsedValue === "object") {
      return "";
    }

    let cleanedText = rawValue.replace(/\{\d*\}/g, " ");

    if (typeof DOMParser !== "undefined") {
      const parser = new DOMParser();
      cleanedText = parser.parseFromString(cleanedText, "text/html").body.textContent || cleanedText;
    } else {
      cleanedText = cleanedText.replace(/<[^>]*>/g, " ");
    }

    return cleanedText.replace(/\s+/g, " ").trim();
  };

  const formatPostSearchItem = (item) => {
    const title = normalizeSearchTitle(item?.title);
    const additionalInfo = normalizeSearchMeta(item?.additionalInfo);
    const description = normalizeSearchMeta(item?.description);

    return {
      title,
      additionalInfo: additionalInfo && additionalInfo !== title ? additionalInfo : "",
      description:
        description && description !== title && description !== additionalInfo
          ? description
          : "",
    };
  };

  const getPostSearchLink = (item) => {
    const normalizedTitle = normalizeSearchTitle(item?.title || "");
    const isHashtagType = item?.type === "Hashtag";
    const looksLikeHashtag = normalizedTitle.startsWith("#") && !!item?.slug;

    if ((isHashtagType || looksLikeHashtag) && item?.slug) {
      return `/hashtag/${encodeURIComponent(item.slug)}`;
    }

    return `/Post/${item?.id}/${item?.slug || ""}`;
  };

  const renderItems = (items, type) => {
    if ((isLoading && page === 1) || !items.length) {
      return renderEmptyState();
    }

    return items.map((item, index) => {
      let itemLink = "#";
      let displayTitle = normalizeSearchTitle(item?.title);
      let displayAdditionalInfo = normalizeSearchMeta(item?.additionalInfo);
      let displayDescription = normalizeSearchMeta(item?.description);

      if (type === "Post") {
        const formattedPost = formatPostSearchItem(item);
        itemLink = getPostSearchLink(item);
        displayTitle = formattedPost.title;
        displayAdditionalInfo = formattedPost.additionalInfo;
        displayDescription = formattedPost.description;
      }
      if (type === "Company") itemLink = `/Company/${item?.id}/${item?.slug || ""}`;
      if (type === "Job") itemLink = `/JobDetail/${item?.id}/${item?.slug || ""}`;
      if (type === "User") itemLink = `/User/${item?.id}`;
      if (type === "Hashtag") itemLink = `/HashTag/${encodeURIComponent(item?.slug || displayTitle || "")}`;
      if (type === "Blog") itemLink = `/Blogs/${item?.slug || item?.id || ""}`;
      if (type === "Article") itemLink = `/Articles/${item?.id}/${item?.slug || ""}`;

      return (
        <>
     
        <Link to={itemLink} key={`${type}-${item?.id || index}`}>
          <div className="suggestion-item d-flex p-3 mb-2 readNotification">
            {item?.image && (
              <img
                src={item?.image}
                alt=""
                width={44}
                height={44}
                style={{ borderRadius: "50px", objectFit: "cover" }}
              />
            )}
            <div className={item?.image ? "ms-3" : ""}>
              <p className="mb-1 fw-bold">{displayTitle}</p>
                {searchHashtag ? "" : 
                  <>
                  {displayAdditionalInfo && <p className="mb-1 f-12">{displayAdditionalInfo}</p>}
                  </>  
                 } 
                {displayDescription && <p className="mb-1 f-12">{displayDescription}</p>}
                {item?.followers > 0 && (
                  <p className="mb-0 f-12">{item?.followers} {t("My Connection.Followers")}</p>
                )}
            </div>
          </div>
        </Link>
        </>
      );
    });
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 1:
        return renderItems(searchUser, "User");
        case 2:
          return renderItems(searchComp, "Company");
          case 3:
        return renderItems(searchJob, "Job");
      case 4:
        return renderItems(searchPost , "Post");
      case 5:
        return renderItems(searchHashtag, "Hashtag");
        case 6:
        return renderItems(searchArticle , "Article");
        case 7:
        return renderItems(searchBlog, "Blog");
      default:
        return renderEmptyState();
    }
  };

  const getActiveItems = () => {
    switch (activeTab) {
      case 1:
        return searchUser;
        case 2:
          return searchComp;
          case 3:
            return searchJob;
      case 4:
        return searchPost;
      case 5:
        return searchHashtag;
        case 6:
          return searchArticle;
        case 7:
          return searchBlog;
      default:
        return [];
    }
  };

  // const getActiveTabTitle = () => {
  //   switch (activeTab) {
  //     case 1:
  //       return t("Company Profile.Jobs");
  //     case 2:
  //       return t("Sidemenubar.Companies");
  //     case 3:
  //       return t("Company Profile.Posts");
  //     case 4:
  //       return t("Sidemenubar.People");
  //     case 5:
  //       return t("Sidemenubar.Blogs");
  //     case 6:
  //       return t("Newsfeed.Hashtag");
  //     default:
  //       return t("SearchJob.See all results");
  //   }
  // };

  // const getSearchPlaceholder = () => {
  //   switch (activeTab) {
  //     case 1:
  //       return "Search jobs";
  //     case 2:
  //       return "Search companies";
  //     case 3:
  //       return "Search posts";
  //     case 4:
  //       return t("People.Search user");
  //     case 5:
  //       return "Search blogs";
  //     case 6:
  //       return "Search hashtags";
  //     default:
  //       return "Search";
  //   }
  // };

  return (
    <>
      <Helmet>
        <title>Search Results | Welinkjobs</title>
        <meta
          name="description"
          content="Browse search results across jobs, companies, posts, people, blogs, and hashtags."
        />
      </Helmet>

      <div>
        <div className="headerSection d-block d-md-flex justify-content-between mt-4">
          <Col md={7}>
            <div className="friendsHeaderText p-2 pt-0 pb-0">
              {/* <h1>{getActiveTabTitle()}</h1> */}
              <h1>{t("SearchJob.See all results")}</h1>
            </div>
          </Col>
          <Col md={5}>
            <div>
              <input
                type="text"
                className="form-control p-2"
                placeholder={t("SearchJob.See all results")}
                // placeholder={getSearchPlaceholder()}
                onChange={(e) => {
                  setHasTabChanged(false);
                  setPage(1);
                  setInputValue(e.target.value);
                }}
                value={inputValue}
              />
            </div>
          </Col>
        </div>

        <div className="job-history deskT d-flex flex-column ">
          <div className="tabButton d-flex p-2 w-100 me-2 connectionTab">
            <NavLink
              onClick={() => goToSearchTab(1)}
              className={`text-center ${activeTab === 1 ? "activeTab" : ""}`}
            >
              <p className="f-14 tabName">{t("Sidemenubar.People")}</p>
            </NavLink>

            <NavLink
              onClick={() => goToSearchTab(2)}
              className={`text-center ${activeTab === 2 ? "activeTab" : ""}`}
            >
              <p className="f-14 tabName">{t("Sidemenubar.Companies")}</p>
            </NavLink>

            <NavLink
              onClick={() => goToSearchTab(3)}
              className={`text-center ${activeTab === 3 ? "activeTab" : ""}`}
            >
              <p className="f-14 tabName">{t("Company Profile.Jobs")}</p>
            </NavLink>

            <NavLink
              onClick={() => goToSearchTab(4)}
              className={`text-center ${activeTab === 4 ? "activeTab" : ""}`}
            >
              <p className="f-14 tabName">{t("Company Profile.Posts")}</p>
            </NavLink>

            <NavLink
              onClick={() => goToSearchTab(5)}
              className={`text-center ${activeTab === 5 ? "activeTab" : ""}`}
            >
              <p className="f-14 tabName">{t("Newsfeed.Hashtag")}</p>
            </NavLink>

            <NavLink
              onClick={() => goToSearchTab(6)}
              className={`text-center ${activeTab === 6 ? "activeTab" : ""}`}
            >
              <p className="f-14 tabName">{t("Newsfeed.Article")}</p>
            </NavLink>
              <NavLink
              onClick={() => goToSearchTab(7)}
              className={`text-center ${activeTab === 7 ? "activeTab" : ""}`}
            >
              <p className="f-14 tabName">{t("Sidemenubar.Blogs")}</p>
            </NavLink>
          </div>

          <div className="tab-content p-2">
           

            {renderTabContent()}

            {nextBatchLoading && (
              <div className="text-center">
                <img src={post?.loader} width={50} alt="Loading" />
              </div>
            )}

            {!hasMoreData && !isLoading && getActiveItems().length > 0 && page > 1 && (
              <p className="text-center mt-3">{t("Newsfeed.No more data available")}</p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SearchResult;
